import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './context/AuthContext';
import Layout from './components/Layout';
import LoadingScreen from './components/LoadingScreen';

const Home         = lazy(() => import('./pages/Home'));
const Gallery      = lazy(() => import('./pages/Gallery'));
const ArtworkDetail= lazy(() => import('./pages/ArtworkDetail'));
const ArtistProfile= lazy(() => import('./pages/ArtistProfile'));
const Login        = lazy(() => import('./pages/Login'));
const Register     = lazy(() => import('./pages/Register'));
const Dashboard    = lazy(() => import('./pages/Dashboard'));
const NewArtwork   = lazy(() => import('./pages/NewArtwork'));
const OrderSuccess = lazy(() => import('./pages/OrderSuccess'));
const Admin        = lazy(() => import('./pages/Admin'));
const Contact      = lazy(() => import('./pages/Contact'));

const ProtectedRoute = ({ children, roles }) => {
  const { user, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  if (!user) return <Navigate to="/connexion" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/" replace />;
  return children;
};

const GuestRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  if (user) return <Navigate to="/" replace />;
  return children;
};

function AppRoutes() {
  return (
    <Suspense fallback={<LoadingScreen />}>
      <Routes>
        {/* Routes publiques avec Layout (nav + footer) */}
        <Route path="/" element={<Layout />}>
          <Route index element={<Home />} />
          <Route path="galerie" element={<Gallery />} />
          <Route path="oeuvre/:uuid" element={<ArtworkDetail />} />
          <Route path="artiste/:username" element={<ArtistProfile />} />
          <Route path="commande/succes" element={<OrderSuccess />} />
          <Route path="contact" element={<Contact />} />
          <Route path="connexion" element={<GuestRoute><Login /></GuestRoute>} />
          <Route path="inscription" element={<GuestRoute><Register /></GuestRoute>} />
          <Route path="tableau-de-bord" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
          <Route path="nouvelle-oeuvre" element={<ProtectedRoute roles={['artist']}><NewArtwork /></ProtectedRoute>} />
          <Route path="artiste/stripe/success" element={<ProtectedRoute roles={['artist']}><Dashboard tab="stripe-success" /></ProtectedRoute>} />
        </Route>

        {/* Admin — hors Layout (pas de nav/footer public) */}
        <Route path="/admin" element={
          <ProtectedRoute roles={['admin']}>
            <Admin />
          </ProtectedRoute>
        } />

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRoutes />
        <Toaster
          position="top-right"
          toastOptions={{
            duration: 4000,
            style: {
              fontFamily: "'Cormorant Garamond', Georgia, serif",
              fontSize: '15px',
              borderRadius: '2px',
              border: '1px solid #D4C5A9',
              background: '#FDFAF5',
              color: '#2C2416',
            },
          }}
        />
      </BrowserRouter>
    </AuthProvider>
  );
}
